import { getWorkingDaysInfo } from "~/utils/workingDays";

export interface SalesGoalRow {
  month: number;
  client_id: string;
  client_name: string;
  client_tax_id: string;
  seller_id: string | null;
  seller_name: string;
  sales_channel_id: string | null;
  sales_channel_name: string;
  goal_amount: number;
  actual_amount: number;
  achievement_pct: number;
}

export interface SellerMonthlyGoals {
  seller_id: string | null;
  seller_name: string;
  months: Record<number, number>;
  actuals: Record<number, number>;
  total: number;
  totalActual: number;
}

export interface ClientGoalRow {
  client_id: string;
  client_name: string;
  client_tax_id: string;
  seller_name: string;
  sales_channel_name: string;
  months: Record<number, number>;
  actuals: Record<number, number>;
  total: number;
  totalActual: number;
}

export const MONTH_LABELS: Record<number, string> = {
  1: "Ene",
  2: "Feb",
  3: "Mar",
  4: "Abr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Ago",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dic",
};

export const MONTH_NAMES: Record<number, string> = {
  1: "Enero",
  2: "Febrero",
  3: "Marzo",
  4: "Abril",
  5: "Mayo",
  6: "Junio",
  7: "Julio",
  8: "Agosto",
  9: "Septiembre",
  10: "Octubre",
  11: "Noviembre",
  12: "Diciembre",
};

export type AnalysisViewMode = "yearly" | "monthly";

export interface AnalysisMetrics {
  expectedToday: number;
  pctAchievement: number;
  estimatedClose: number;
  estimatedDaily: number;
  actualDaily: number;
}

export interface YearlyAggregateMetrics {
  deberiaLlevar: number;
  pctAchievement: number;
  cierreEstimado: number;
}

export function computeYearlyAggregates(
  months: Record<number, number>,
  actuals: Record<number, number>,
  year: number,
  currentMonth: number,
  currentMonthWd?: {
    totalWorkingDays: number;
    elapsedWorkingDays: number;
    remainingWorkingDays: number;
  }
): YearlyAggregateMetrics {
  let deberiaLlevar = 0;
  let cierreEstimado = 0;
  let totalActual = 0;

  for (let m = 1; m <= 12; m++) {
    const projection = months[m] || 0;
    const actual = actuals[m] || 0;
    totalActual += actual;

    if (currentMonth > 0 && m < currentMonth) {
      deberiaLlevar += projection;
      cierreEstimado += actual;
    } else if (m === currentMonth && currentMonth > 0) {
      const wd = currentMonthWd ?? getWorkingDaysInfo(year, m);
      const ratio =
        wd.totalWorkingDays > 0
          ? wd.elapsedWorkingDays / wd.totalWorkingDays
          : 0;
      deberiaLlevar += projection * ratio;
      const estDaily =
        wd.totalWorkingDays > 0 ? projection / wd.totalWorkingDays : 0;
      cierreEstimado += actual + estDaily * wd.remainingWorkingDays;
    } else {
      cierreEstimado += projection;
    }
  }

  const pctAchievement =
    deberiaLlevar > 0 ? (totalActual / deberiaLlevar) * 100 : 0;

  return { deberiaLlevar, pctAchievement, cierreEstimado };
}

export function computeAnalysisMetrics(
  projection: number,
  sales: number,
  totalWorkingDays: number,
  elapsedWorkingDays: number,
  remainingWorkingDays: number
): AnalysisMetrics {
  const estimatedDaily =
    totalWorkingDays > 0 ? projection / totalWorkingDays : 0;
  const actualDaily = elapsedWorkingDays > 0 ? sales / elapsedWorkingDays : 0;
  const expectedToday =
    totalWorkingDays > 0
      ? projection * (elapsedWorkingDays / totalWorkingDays)
      : 0;
  const pctAchievement =
    expectedToday > 0 ? (sales / expectedToday) * 100 : sales > 0 ? 100 : 0;
  const estimatedClose = sales + estimatedDaily * remainingWorkingDays;

  return {
    expectedToday,
    pctAchievement,
    estimatedClose,
    estimatedDaily,
    actualDaily,
  };
}
